import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
// Import your central theme file
import { THEME } from "./colorFile"; 

export default function TopBar({ title, user, firstName, handleLogout }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // Helper to handle navigation and close menu (useful for mobile)
  const handleNav = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  return (
    <header
      id="topbar"
      className="w-full bg-white border-b border-gray-300 shadow-sm relative z-50"
    >
      <nav className="flex justify-between items-center max-w-7xl mx-auto px-4 sm:px-6 py-4">
        
        {/* --- LOGO WITH POP & SHAKE --- */}
        <div
          onClick={() => navigate("/")}
          className="group cursor-pointer"
        >
          <div className="text-2xl font-extrabold text-orange-600 bg-white transition-transform duration-200 ease-in-out transform group-hover:scale-110 group-hover:animate-wiggle inline-block">
            {title}
          </div>
        </div>

        {/* --- DESKTOP NAV --- */}
        <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
          
          {/* Standard Link: Practice */}
          <button
            onClick={() => navigate("/practice")}
            className={`${THEME.primary2} ${THEME.secondary2} transition-colors`}
          >
            Practice
          </button>

          {/* 🔻 DROPDOWN MENU: Features 🔻 */}
          <div className="relative group">
            <button
              className={`${THEME.primary2} ${THEME.secondary2} flex items-center transition-colors`}
              onClick={() => {}}
            >
              More
              {/* Chevron Icon */}
              <svg className="w-4 h-4 ml-1 group-hover:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </button>

            {/* The Dropdown Panel */}
            <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-left">
              <div className="py-2 flex flex-col">
                <button
                  onClick={() => navigate("/pricing")}
                  className={`text-left px-4 py-2 text-sm ${THEME.primary3} ${THEME.secondary3} hover:bg-gray-50`}
                >
                  Pricing & Refund
                </button>
                <button
                  onClick={() => navigate("/privacy")}
                  className={`text-left px-4 py-2 text-sm ${THEME.primary3} ${THEME.secondary3} hover:bg-gray-50`}
                >
                  Privacy Policy
                </button>
                <button
                  onClick={() => navigate("/terms")}
                  className={`text-left px-4 py-2 text-sm ${THEME.primary3} ${THEME.secondary3} hover:bg-gray-50`}
                >
                  Terms of Service
                </button>
              </div>
            </div>
          </div>

          {/* Standard Link: About */}
          <button
            onClick={() => navigate("/about")}
            className={`${THEME.primary2} ${THEME.secondary2} transition-colors`}
          >
            About
          </button>

          {/* Standard Link: My Tests */}
          <button
            onClick={() => navigate("/history")}
            className={`${THEME.primary2} ${THEME.secondary2} transition-colors`}
          >
            My Tests
          </button>

          {/* --- USER ACTIONS --- */}
          <div className="flex items-center space-x-6 border-l pl-6 border-gray-200">
            {user ? (
              <>
                <span className={`font-semibold ${THEME.primary1} ${THEME.secondary1} cursor-default`}>
                  {firstName}
                </span>
                <button
                  onClick={handleLogout}
                  className={`text-sm font-medium ${THEME.primary1} ${THEME.secondary1} transition-colors`}
                >
                  Log Out
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className={`font-medium ${THEME.primary1} ${THEME.secondary1} transition-colors`}
                >
                  Sign in
                </button>
                <button
                  onClick={() => navigate("/signup")}
                  className={`${THEME.signupBtnBg} text-white px-5 py-2 rounded-full text-sm font-medium shadow-md transition-transform hover:scale-105`}
                >
                  Sign up
                </button>
              </>
            )}
          </div>
        </div>

        {/* --- HAMBURGER BUTTON --- */}
        <button
          className="md:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100 transition"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
            )}
          </svg>
        </button>
      </nav>

      {/* --- MOBILE MENU --- */}
      <div
        className={`md:hidden fixed top-[72px] right-0 w-72 h-[calc(100vh-72px)] bg-white shadow-2xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out z-40 ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col p-6 space-y-6 overflow-y-auto h-full">
          <div className="flex flex-col space-y-4">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Menu</h3>
            <button onClick={() => handleNav("/practice")} className="text-gray-700 font-medium hover:text-orange-600 text-left text-lg">Practice</button>
            <button onClick={() => handleNav("/about")} className="text-gray-700 font-medium hover:text-orange-600 text-left text-lg">About</button>
            <button onClick={() => handleNav("/history")} className="text-gray-700 font-medium hover:text-orange-600 text-left text-lg">My Tests</button>
          </div>

          <div className="flex flex-col space-y-3 pt-4 border-t border-gray-100">
             <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">More</h3>
             <button onClick={() => handleNav("/pricing")} className="text-gray-600 hover:text-black text-left">Pricing</button>
             <button onClick={() => handleNav("/privacy")} className="text-gray-600 hover:text-black text-left">Privacy Policy</button>
             <button onClick={() => handleNav("/terms")} className="text-gray-600 hover:text-black text-left">Terms of Service</button>
          </div>

          <div className="mt-auto pt-6 border-t border-gray-200">
            {user ? (
              <div className="flex flex-col space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-xl">
                    {firstName.charAt(0)}
                  </div>
                  <span className="text-gray-900 font-semibold text-lg">{firstName}</span>
                </div>
                <button onClick={handleLogout} className="w-full bg-gray-100 text-gray-800 py-3 rounded-xl font-medium hover:bg-gray-200">Log Out</button>
              </div>
            ) : (
              <div className="flex flex-col space-y-3">
                <button onClick={() => handleNav("/login")} className="w-full text-center py-3 text-gray-700 font-medium hover:text-black border border-gray-200 rounded-xl">Sign in</button>
                <button onClick={() => handleNav("/signup")} className={`w-full text-center py-3 text-white font-medium rounded-xl shadow-lg ${THEME.signupBtnBg}`}>Sign up</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}